import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { first_name: "", last_name: "", date_of_birth: "", email: "" };

export default function PatientForm({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.date_of_birth) {
      return toast.error("Nom, prénom et date de naissance sont requis");
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return toast.error("Email invalide");
    }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        date_of_birth: form.date_of_birth,
        email: form.email ? form.email.trim() : null,
      };
      const { data } = initial?.patient_id
        ? await api.put(`/patient-records/${initial.patient_id}`, payload)
        : await api.post("/patient-records", payload);
      toast.success(initial?.patient_id ? "Patient mis à jour" : "Patient créé");
      onSaved(data);
      setForm(EMPTY);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setForm(EMPTY); onClose(); } }}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {initial?.patient_id ? "Modifier le patient" : "Nouveau patient"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Prénom</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="mt-2 bg-white border-[#E1E9F0]" data-testid="patient-first-name" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Nom</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="mt-2 bg-white border-[#E1E9F0]" data-testid="patient-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Date de naissance</Label>
            <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              className="mt-2 bg-white border-[#E1E9F0]" data-testid="patient-dob" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Email <span className="normal-case tracking-normal text-[#5F758F]">(optionnel)</span></Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 bg-white border-[#E1E9F0]" placeholder="patient@exemple.com" data-testid="patient-email" />
            <p className="text-xs text-[#5F758F] pt-1.5">Vous pourrez l'ajouter plus tard, au moment de l'envoi.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-md" onClick={() => { setForm(EMPTY); onClose(); }}>Annuler</Button>
          <Button className="rounded-md bg-[#3A8FA5] hover:bg-[#164E5F] text-white" onClick={submit} disabled={busy} data-testid="patient-save">
            {busy ? "Enregistrement…" : (initial?.patient_id ? "Enregistrer" : "Créer le patient")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
